import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import { Body } from '@/components/Typography';
import {
  PoolMemberRow,
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';

// ViolationActionModal layout with the reason OPTIONAL (OQ-2, decided
// 2026-07-16): the field audits when ops types one, but never blocks the
// remove. Removal is a soft episode close server-side (P11).

type Props = {
  poolId: number;
  member: PoolMemberRow;
  onClose: () => void;
  onSuccess: () => void;
};

const RemoveMemberModal = ({ poolId, member, onClose, onSuccess }: Props) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await PreferredPoolsAPI.removeMember(
        poolId,
        member.membership_id,
        reason.trim() || undefined,
      );
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(extractPoolsError(err, 'Failed to remove worker from pool'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Remove from pool"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          Remove <strong>{member.worker.name}</strong> from this pool? Their
          membership history is kept, and they can be re-added later.
        </Body>
      </Card.Section>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <FormElement label="Reason (optional)">
          <TextArea
            placeholder="Add a reason for the audit trail."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Remove from pool"
            isLoading={loading}
            label="Remove"
            status="danger"
            onClick={handleSubmit}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default RemoveMemberModal;
